import { useCallback, useState } from 'react';

import { readChoice, rememberChoice } from './rememberedChoice';
import { OFFER, OPENING, SubjectType } from './types';

export const SUBJECT_TYPE_STORAGE_KEY = 'approvals.rulesSubjectType';

const isSubjectType = (value: string | null): value is SubjectType =>
  value === OFFER || value === OPENING;

/**
 * Which side of the rule table is showing, and how that survives leaving the
 * page (AXHE-4465).
 *
 * localStorage alone, unlike `useApprovalsTab`, which reads `?tab=` ahead of it
 * because `opening_approvals` takes a tab in its query string and the monolith
 * deep-links into it. Nothing links to a side of this filter, and Design
 * Requirements §2 asks for one thing: the toggle "should remember the previous
 * view for the user to return to".
 *
 * Read once, on the first render — re-reading each render would let the write
 * below reorder its own source of truth.
 *
 * A stored value naming neither side falls back to Offers, the design's
 * default, rather than filtering by a token the server would reject.
 * `SubjectType` is derived from the generated client, so a browser can be
 * holding a value this build no longer has.
 */
export function useSubjectTypeView(): [
  SubjectType,
  (next: SubjectType) => void,
] {
  const [subjectType, setSubjectType] = useState<SubjectType>(() => {
    const remembered = readChoice(SUBJECT_TYPE_STORAGE_KEY);
    return isSubjectType(remembered) ? remembered : OFFER;
  });

  const select = useCallback((next: SubjectType) => {
    rememberChoice(SUBJECT_TYPE_STORAGE_KEY, next);
    setSubjectType(next);
  }, []);

  return [subjectType, select];
}
